import accepts from "attr-accept";
import Compressor from "compressorjs";

export const supportMultiple =
  typeof document !== "undefined" && document && document.createElement
    ? "multiple" in document.createElement("input")
    : true;

export const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
export const MAX_HEIC_SIZE = 10 * 1024 * 1024; // 10MB
export const whiteListTypes = ["image/png", "image/jpg", "image/jpeg"];

export function getDataTransferItems(event) {
  let dataTransferItemsList = [];
  if (event.dataTransfer) {
    const dt = event.dataTransfer;

    if (dt.files && dt.files.length) {
      dataTransferItemsList = dt.files;
    } else if (dt.items && dt.items.length) {
      // During the drag even the dataTransfer.files is null
      // but Chrome implements some drag store, which is accesible via dataTransfer.items
      dataTransferItemsList = dt.items;
    }
  } else if (event.target && event.target.files) {
    dataTransferItemsList = event.target.files;
  }

  // Convert from DataTransferItemsList to the native Array
  return Array.prototype.slice.call(dataTransferItemsList);
}

// Firefox versions prior to 53 return a bogus MIME type for every file drag, so dragovers with
// that MIME type will always be accepted
export function fileAccepted(file, accept) {
  return file.type === "application/x-moz-file" || accepts(file, accept);
}

export function validateFilename(filename) {
  const invalidChars = /[!@#$%^&*()+={}[\]|\\:;"'<>?/,]/;
  return !invalidChars.test(filename);
}

export function fileMatchSize(file, maxSize, minSize) {
  return file.size <= maxSize && file.size >= minSize;
}

export function allFilesAccepted(files, accept) {
  return files.every(file => fileAccepted(file, accept));
}

// allow the entire document to be a drag target
export function onDocumentDragOver(evt) {
  evt.preventDefault();
}

function isIe(userAgent) {
  return userAgent.indexOf("MSIE") !== -1 || userAgent.indexOf("Trident/") !== -1;
}

function isEdge(userAgent) {
  return userAgent.indexOf("Edge/") !== -1;
}

export function isIeOrEdge(userAgent = window.navigator.userAgent) {
  return isIe(userAgent) || isEdge(userAgent);
}

export const isNarrowScreen = window.matchMedia("(max-width: 680px)").matches;

export function detectFileType(file) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onloadend = function(evt) {
        try {
          if (evt.target.readyState === FileReader.DONE) {
            const uint = new Uint8Array(evt.target.result);
            const fileTypes = [
              { type: "jfif", magic: [0x4a, 0x46, 0x49, 0x46], offset: 6 }, // JPEG File Interchange Format

              // HEIC/HEIF variants
              { type: "heic", magic: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x63], offset: 4 }, // HEIC: HEVC Image
              { type: "heic", magic: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x78], offset: 4 }, // HEIX: HEVC Image with alpha
              { type: "heic", magic: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x76, 0x63], offset: 4 }, // HEVC: HEVC Video
              { type: "heic", magic: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x76, 0x6d], offset: 4 }, // HEVM: HEVC Video with alpha
              { type: "heif", magic: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x66], offset: 4 }, // HEIF: Generic HEIF
              { type: "heif", magic: [0x66, 0x74, 0x79, 0x70, 0x6d, 0x69, 0x66, 0x31], offset: 4 }, // MIF1: Image Multi
              { type: "heif", magic: [0x66, 0x74, 0x79, 0x70, 0x6d, 0x73, 0x66, 0x31], offset: 4 }, // MSF1: Image Sequence
              { type: "heif", magic: [0x66, 0x74, 0x79, 0x70, 0x61, 0x76, 0x69, 0x66], offset: 4 }, // AVIF: AV1 Image Format
            ];

            for (let fileType of fileTypes) {
              if (
                uint
                  .subarray(fileType.offset, fileType.offset + fileType.magic.length)
                  .every((value, index) => value === fileType.magic[index])
              ) {
                resolve(fileType.type);
                return;
              }
            }

            resolve(file.type); // Fallback to the file's reported MIME type
          }
        } catch (err) {
          reject("Error processing file type: " + err);
        }
      };
      const blob = file.slice(0, 12); // Read only the first 12 bytes
      reader.readAsArrayBuffer(blob);
    } catch (err) {
      reject("Error detecting file type: " + err);
    }
  });
}
export async function resizeImage(file, MAX_FILE_SIZE) {
  if (file.size <= MAX_FILE_SIZE) {
    return file;
  }

  const options = {
    quality: 0.8,
    maxWidth: 1600,
    maxHeight: 1600,
    convertSize: MAX_FILE_SIZE,
  };

  try {
    return await new Promise((resolve, reject) => {
      new Compressor(file, {
        ...options,
        success(result) {
          resolve(result);
        },
        error(err) {
          reject("Error compressing image: " + err.message);
        },
      });
    });
  } catch (e) {
    throw new Error("Error initializing compressor: " + e.message);
  }
}
export function generatePreview(file, disablePreview) {
  if (!disablePreview) {
    try {
      file.preview = window.URL.createObjectURL(file); // eslint-disable-line no-param-reassign
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to generate preview for file", file, err); // eslint-disable-line no-console
      }
    }
  }
}

export const renameFileToJpeg = filename => {
  if (!filename || typeof filename !== "string") {
    return "unnamed.jpeg";
  }

  const extensionRegex = /\.[^/.]+$/;

  // If the file has no extension, add .jpeg
  if (!extensionRegex.exec(filename)) {
    return filename + ".jpeg";
  } else {
    // If the file has an extension, replace it with .jpeg
    return filename.replace(extensionRegex, ".jpeg");
  }
};



// WEBPACK FOOTER //
// ./components/common/file-uploader/utils.js